/**
 * TanStack Query hooks for database connection management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  databaseConnectionsApi,
  accountDatabasesApi,
  type CreateDatabaseConnectionRequest,
  type UpdateDatabaseConnectionRequest,
} from '../api';

// ============================================================================
// Query Keys
// ============================================================================

export const dbConnectionKeys = {
  all: ['database-connections'] as const,
  lists: () => [...dbConnectionKeys.all, 'list'] as const,
  list: (siteSlug: string) => [...dbConnectionKeys.lists(), siteSlug] as const,
  details: () => [...dbConnectionKeys.all, 'detail'] as const,
  detail: (siteSlug: string, id: number) =>
    [...dbConnectionKeys.details(), siteSlug, id] as const,
};

// ============================================================================
// Database Connection Hooks
// ============================================================================

/** Fetch all database connections for a site */
export function useDatabaseConnections(siteSlug: string) {
  return useQuery({
    queryKey: dbConnectionKeys.list(siteSlug),
    queryFn: () => databaseConnectionsApi.list(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Fetch a single database connection */
export function useDatabaseConnection(siteSlug: string, id: number) {
  return useQuery({
    queryKey: dbConnectionKeys.detail(siteSlug, id),
    queryFn: () => databaseConnectionsApi.get(siteSlug, id),
    enabled: !!siteSlug && !!id,
  });
}

/** Create a new database connection */
export function useCreateDatabaseConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteSlug,
      data,
    }: {
      siteSlug: string;
      data: CreateDatabaseConnectionRequest;
    }) => databaseConnectionsApi.create(siteSlug, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: dbConnectionKeys.list(variables.siteSlug),
      });
    },
  });
}

/** Update a database connection */
export function useUpdateDatabaseConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteSlug,
      id,
      data,
    }: {
      siteSlug: string;
      id: number;
      data: UpdateDatabaseConnectionRequest;
    }) => databaseConnectionsApi.update(siteSlug, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: dbConnectionKeys.list(variables.siteSlug),
      });
    },
  });
}

/** Delete a database connection */
export function useDeleteDatabaseConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteSlug, id }: { siteSlug: string; id: number }) =>
      databaseConnectionsApi.delete(siteSlug, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: dbConnectionKeys.list(variables.siteSlug),
      });
    },
  });
}

/** Test a database connection */
export function useTestDatabaseConnection() {
  return useMutation({
    mutationFn: ({ siteSlug, id }: { siteSlug: string; id: number }) =>
      databaseConnectionsApi.test(siteSlug, id),
  });
}

// ============================================================================
// Account-level Database Connection Hooks (no site context)
// ============================================================================

export const accountDbKeys = {
  all: ['account-databases'] as const,
  list: () => [...accountDbKeys.all, 'list'] as const,
  detail: (id: number) => [...accountDbKeys.all, 'detail', id] as const,
};

/** Fetch all account-level database connections */
export function useAccountDatabases() {
  return useQuery({
    queryKey: accountDbKeys.list(),
    queryFn: () => accountDatabasesApi.list(),
  });
}

/** Fetch a single account-level database connection */
export function useAccountDatabase(id: number) {
  return useQuery({
    queryKey: accountDbKeys.detail(id),
    queryFn: () => accountDatabasesApi.get(id),
    enabled: !!id,
  });
}

/** Create a new account-level database connection */
export function useCreateAccountDatabase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDatabaseConnectionRequest) =>
      accountDatabasesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountDbKeys.list(),
      });
    },
  });
}

/** Update an account-level database connection */
export function useUpdateAccountDatabase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDatabaseConnectionRequest }) =>
      accountDatabasesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountDbKeys.list(),
      });
    },
  });
}

/** Delete an account-level database connection */
export function useDeleteAccountDatabase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => accountDatabasesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountDbKeys.list(),
      });
    },
  });
}

/** Test an account-level database connection */
export function useTestAccountDatabase() {
  return useMutation({
    mutationFn: (id: number) => accountDatabasesApi.test(id),
  });
}

/** Test connection details without saving (inline test) */
export function useTestInlineConnection() {
  return useMutation({
    mutationFn: (data: CreateDatabaseConnectionRequest) =>
      accountDatabasesApi.testInline(data),
  });
}
